import { connect } from 'react-redux';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { TilesLayer } from '@tcc/treadstone';
import { BASE_FIELDVIEW_URL as BASE_URL } from '../../../constants/urls';
import { getFvpCacheBuster } from '../../../selectors/layerSelectors';
import { userSelector } from '../../../selectors/userSelectors';
import { getFieldBounds } from '../../../selectors/fieldSelectors';
import { logDmbError } from '../../../utils/error';
import { getCurrentLayerId, getCurrentSeason } from '../../../selectors';
import { getArdDataFeatureSelector } from '../../../selectors/featureSelectors';

export class HarvestAndPlantingMap extends Component {
  shouldComponentUpdate(nextProps) {
    return JSON.stringify(this.props) !== JSON.stringify(nextProps);
  }

  onTileError = (error) => {
    const tileUrl = error.target.options.url || error.target._url;
    logDmbError(`Tile image not loaded: ${tileUrl}`);
  };

  render() {
    const {
      agVizTileUrl,
      agVizVersion,
      cacheBuster,
      crop,
      dates,
      fieldBounds,
      fieldUuid,
      layer,
      user,
    } = this.props;
    const { endDate, startDate, year } = dates;

    // If Ag-Viz feature flag is enabled and we have an Ag-Viz tile URL, use it
    if (agVizTileUrl) {
      // Add version parameter for cache busting
      // Only add version parameter if we have a real version (not "UNKNOWN")
      const urlWithCacheBuster = agVizVersion && agVizVersion !== 'UNKNOWN'
        ? `${agVizTileUrl}&v=${agVizVersion}`
        : agVizTileUrl;
      return (
        <TilesLayer
          bounds={fieldBounds}
          onTileError={(error) => this.onTileError(error)}
          url={urlWithCacheBuster}
          zIndex={2}
          maxNativeZoom={20}
          maxZoom={20}
        />
      );
    }

    if (!cacheBuster) {
      return null;
    }

    // Original FV-Spatial tile URL construction
    const lastUrlPart = `{z}/{x}/{y}.png?startTime=${startDate}&endTime=${endDate}&cacheBuster=${cacheBuster}`;

    const firstPartUrl = `${BASE_URL}/tile/v3/${user.uuid}/${fieldUuid}/${year}/${crop}/${layer}/`

    const url = firstPartUrl + lastUrlPart;

    return (
      <TilesLayer
        bounds={fieldBounds}
        onTileError={(error) => this.onTileError(error)}
        url={url}
        zIndex={2}
        maxNativeZoom={20}
        maxZoom={20}
      />
    );
  }
}

HarvestAndPlantingMap.propTypes = {
  agVizTileUrl: PropTypes.string,
  agVizVersion: PropTypes.string,
  cacheBuster: PropTypes.string,
  crop: PropTypes.string.isRequired,
  dates: PropTypes.object.isRequired,
  fieldBounds: PropTypes.array.isRequired,
  fieldUuid: PropTypes.string.isRequired,
  layer: PropTypes.string.isRequired,
  user: PropTypes.object,
};

export function mapStateToProps(state, props) {
  const activeMapLayer = getCurrentLayerId(state, props);
  const activeSeason = getCurrentSeason(state, props);
  
  const agVizEnabled = getArdDataFeatureSelector(state);

  // Only read Ag-Viz tile URL / version when the Ag-Viz feature flag is enabled,
  // so users without the flag keep the original FV-Spatial tile path untouched.
  const agVizLayerState = agVizEnabled && activeSeason && activeMapLayer
    ? state.layers.list?.[activeSeason]?.[activeMapLayer]
    : null;

  const agVizTileUrl = agVizLayerState?.agVizTileUrl ?? null;

  const agVizVersion = agVizLayerState?.agVizVersion ?? null;

  return {
    agVizTileUrl,
    agVizVersion,
    cacheBuster: getFvpCacheBuster(state, props),
    fieldBounds: getFieldBounds(state),
    fieldUuid: state.field.uuid?.toUpperCase() ?? '',
    layer: activeMapLayer,
    user: userSelector(state)
  };
}

export default connect(mapStateToProps)(HarvestAndPlantingMap);
